import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Button, Stack, Typography } from '@mui/material'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useDeleteAccountGroupMutation, useGetAccountGroupQuery, useUpdateAccountGroupMutation } from '@/core/api'
import { useGetAdminAccountsQuery } from '@/core/services/admin'
import { useGetPoliciesQuery } from '@/core/services/policy'

import { getApiErrorMessage } from '@/utils/errors'
import { isApiKey, isStaff } from '@/utils/type-guards'

import GroupForm from './components/GroupForm'
import NotFoundPage from '@/pages/error/NotFoundPage'

export default function GroupPage() {
  const navigate = useNavigate()
  const { groupId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: accountGroup, isError: isGetAccountGroupError, isLoading: isFetchingAccountGroup } = useGetAccountGroupQuery(groupId ?? '', { skip: !groupId })
  const { data: accounts, refetch: refetchAccounts } = useGetAdminAccountsQuery()
  const { data: policies } = useGetPoliciesQuery()
  const [updateAccountGroup, { isLoading: isUpdatingAccountGroup }] = useUpdateAccountGroupMutation()
  const [deleteAccountGroup, { isLoading: isDeletingAccountGroup }] = useDeleteAccountGroupMutation()

  const [editable, setEditable] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const policiesData = useMemo(
    () =>
      policies
        ?.filter((policy) => (editable ? true : accountGroup?.policies.includes(policy._id)))
        .map((policy) => ({
          id: policy._id,
          name: policy.name,
          description: policy.description,
        })),
    [accountGroup, editable, policies]
  )

  const accountsData = useMemo(
    () =>
      accounts
        ?.filter((account) => (editable || !groupId ? isStaff(account) || isApiKey(account) : account.groups?.includes(groupId)))
        .map((account) => ({
          id: account._id,
          name: isStaff(account) ? `${account.userInfo.name} ${account.userInfo.surname}` : isApiKey(account) ? account.name : '',
          status: isStaff(account) ? account.status : isApiKey(account) ? 'API KEY' : '',
        })),
    [accounts, editable, groupId]
  )

  const initialValues = useMemo(
    () =>
      accountGroup
        ? {
          accounts: accounts?.filter((account) => account.groups?.includes(accountGroup._id)).map((account) => account._id) ?? [],
          name: accountGroup.name,
          policies: policies?.filter((policy) => accountGroup.policies.includes(policy._id)).map((policy) => policy._id) ?? [],
        }
        : undefined,
    [accounts, accountGroup, policies]
  )

  if (isFetchingAccountGroup) {
    return null
  }

  if (isGetAccountGroupError) return <NotFoundPage />

  if (!accountGroup) return null

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{accountGroup.name}</Typography>
          <Stack direction="row" spacing={2}>
            <Button color="error" onClick={() => setOpenDeleteConfirmationDialog(true)} variant="contained">
              {t('common:button.delete.label')}
            </Button>
            <Button onClick={() => setEditable(!editable)} variant="contained">
              {!editable ? t('common:button.modify.label') : t('common:button.cancel.label')}
            </Button>
          </Stack>
        </Stack>
        <Stack spacing={3}>
          <GroupForm
            key={editable ? 'editable' : 'readOnly'}
            accounts={accountsData}
            initialValues={initialValues}
            isSubmitting={isUpdatingAccountGroup}
            onSubmit={async (values) => {
              try {
                await updateAccountGroup({ id: accountGroup._id, ...values }).unwrap()
                refetchAccounts()
                setEditable(false)
              } catch (error) {
                enqueueSnackbar(getApiErrorMessage(error, t('notification.accountGroup.update.error')), { variant: 'error' })
              }
            }}
            policies={policiesData}
            readOnly={!editable}
          />
        </Stack>
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingAccountGroup}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          try {
            await deleteAccountGroup(accountGroup._id).unwrap()
            setOpenDeleteConfirmationDialog(false)
            navigate('/admin/groups')
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.apiKey.delete.error')), { variant: 'error' })
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.accountGroup.title')}
        message={t('dialog.deleteConfirmation.accountGroup.message')}
      />
    </PageContainer>
  )
}
